import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { ButtonStyle } from '@constants/button/1.0';
import { MessageType } from '@constants/message/1.0';
import { DateService } from '@services/utilities/1.0/date.service';
import { TceLocaleService } from '@services/tce-locale/1.0/tce-locale.service';
import { Subject, takeUntil } from 'rxjs';
import { TimeZone } from '@interfaces/timeZone/1.0';
import { DistributionJobRequest, DistributionRule, Job, Segment } from '@generated/tcea/models';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { MatTableDataSource } from '@angular/material/table';
import { ThemePalette, MatOption } from '@angular/material/core';
import {
  NGX_MAT_DATE_FORMATS,
  NgxMatDateFormats,
  NgxMatDatetimepicker,
  NgxMatDatetimePickerModule,
} from '@nng-components/angular-material-datetime-picker';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { NgIf, NgClass, NgFor } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatDatepickerToggle } from '@angular/material/datepicker';
import { MatIconButton } from '@angular/material/button';
import { LoadingComponent } from '../loading/1.0/loading.component';
import { ButtonComponent } from '../button/1.0/button.component';
import { TranslateModule } from '@ngx-translate/core';

export const CUSTOM_DATE_FORMATS: NgxMatDateFormats = {
  parse: {
    dateInput: 'M/d/yyyy, h:mm:ss a',
  },
  display: {
    dateInput: 'M/d/yyyy, h:mm:ss a',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'MMMM d, yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export interface DistributionJobDialogData {
  job: Job;
  segmentsList: MatTableDataSource<Segment>;
  title: string;
}

@Component({
  selector: 'txp-distribution-job-dialog',
  templateUrl: './distribution-job-dialog.component.html',
  styleUrls: ['./distribution-job-dialog.component.scss'],
  providers: [{ provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    NgIf,
    MatInput,
    MatSelect,
    NgClass,
    NgFor,
    MatOption,
    MatError,
    NgxMatDatetimePickerModule,
    MatDatepickerToggle,
    MatSuffix,
    MatIconButton,
    LoadingComponent,
    MatDialogActions,
    ButtonComponent,
    TranslateModule,
  ],
})
export class DistributionJobDialogComponent implements OnInit {
  @ViewChild('jobStartDatePicker') jobStartDatePicker: NgxMatDatetimepicker<any>;

  form: UntypedFormGroup;
  buttonStyle = ButtonStyle;
  loadingType = LoadingType; // expose enum to template
  loadingColor = LoadingColor;
  public color: ThemePalette = 'primary';
  disableContinueBtn = true;
  isSaving: boolean;
  showError: boolean;
  messageType = MessageType;
  errorMessage: string;
  addingJob: boolean;
  editingJob: Job = null;
  jobToAdd: UntypedFormGroup;
  showDataLoadError: boolean;
  timeZones: TimeZone[];
  selectedTimeZone: TimeZone[];
  filteredTimeZones: TimeZone[];
  selectedSegments: Segment[];
  segmentSearch = false;
  segmentsList: MatTableDataSource<Segment> = new MatTableDataSource<Segment>();
  rule: DistributionRule;
  inputFocused = false;
  minDate = null;
  jobsData: MatTableDataSource<DistributionJobRequest> =
    new MatTableDataSource<DistributionJobRequest>();

  private unsubscribe = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<DistributionJobDialogComponent>,
    private dateService: DateService,
    private tceLocaleService: TceLocaleService,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DistributionJobDialogData,
  ) {
    this.segmentsList = this.data.segmentsList;
    this.jobToAdd = formBuilder.group({
      date: new UntypedFormControl({ value: '', disabled: true }, [
        this.jobDateValidator.bind(this),
      ]),
      timeZone: new UntypedFormControl('', Validators.required),
      filterTimezones: new UntypedFormControl(),
      segments: new UntypedFormControl([], Validators.required),
      filterSegments: new UntypedFormControl(),
    });
    dialogRef.disableClose = true;
  }

  jobDateValidator(formControl: AbstractControl): ValidationErrors | undefined {
    if (!formControl.value) {
      return null;
    }

    const timeZoneToUse = this.jobToAdd?.value.timeZone;
    const now = new Date();
    const nowInTz = this.dateService.convertToTimeZone(now, timeZoneToUse);
    const after10Minutes = this.dateService.add(nowInTz, 10, 'minute');
    const dateToCheck = this.dateService.convertToTimeZone(formControl.value, timeZoneToUse);

    if (dateToCheck.getTime() >= after10Minutes.getTime()) {
      return null;
    }

    return { invalid: true };
  }

  onToggleJobForm() {
    this.jobToAdd.reset();

    if (this.editingJob) {
      this.editingJob = undefined;
    } else {
      this.addingJob = !this.addingJob;
    }
  }

  ngOnInit(): void {
    this.loadLocaleData();
    // Subscribe to timezone change
    this.jobToAdd
      .get('timeZone')
      .valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe((newTimeZone) => {
        this.handleTimeZoneChange(newTimeZone);
      });

    // If editing, patch values and manually handle timezone logic
    if (this.data.job) {
      const { startTime, audienceIds } = this.data.job as any;
      this.jobToAdd.patchValue({
        date: startTime,
        timeZone: this.dateService.getLocalTimeZone(),
        segments: audienceIds,
      });

      // Ensure logic is triggered manually for pre-set values
      this.handleTimeZoneChange(this.dateService.getLocalTimeZone());

      this.editingJob = this.data.job;
    }
  }

  loadLocaleData() {
    this.tceLocaleService
      .getLocaleData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (localeData) => {
          this.timeZones = localeData.timeZones;
          this.filteredTimeZones = [...localeData.timeZones];
        },
        error: () => {
          this.showDataLoadError = true;
        },
      });
  }

  filterItems(searchText: string, list: any[], key: string): any[] {
    return list.filter((item) => item[key]?.toLowerCase()?.includes(searchText?.toLowerCase()));
  }

  private handleTimeZoneChange(newTimeZone: string): void {
    const dateControl = this.jobToAdd.get('date');

    if (newTimeZone) {
      if (dateControl.disabled) {
        dateControl.enable();
      }
    } else {
      dateControl.disable();
    }

    const now = new Date();
    const nowInTz = this.dateService.convertToTimeZone(now, newTimeZone);
    this.minDate = this.dateService.format(
      this.dateService.startOf(nowInTz, 'day'),
      'YYYY-MM-DD HH:mm:ss',
    );
  }

  onSaveJob() {
    const jobToAddControl = this.jobToAdd;

    if (jobToAddControl.invalid) {
      jobToAddControl.markAllAsTouched();
      return;
    }

    const audienceIds = jobToAddControl.value.segments;
    const dateInTz = new Date(jobToAddControl.value.date);
    const startTime = this.dateService.formatLocalDateTime(
      this.dateService.convertToTimeZone(dateInTz, jobToAddControl.value.timeZone),
    );

    const newJobData: DistributionJobRequest = {
      audienceIds,
      startTime,
      ruleId: this.rule?.id,
      id: this.editingJob?.id || '',
    };

    jobToAddControl.reset();

    if (newJobData) {
      this.isSaving = true;
      this.showError = false;
      this.dialogRef.close(newJobData);
    } else {
      return this.dialogRef.close(newJobData);
    }
  }

  onCancel() {
    this.dialogRef.close();
  }
}
